import React, { useState, useRef, useEffect } from 'react';
import { useCart } from '../context/CartContext';
import ProductModal from '../components/ProductModal';
import FloatingCartBar from '../components/FloatingCartBar';
import CartDrawer from '../components/CartDrawer';
import TemporaryNotice from '../components/TemporaryNotice';
import { useRestaurantStatus } from '../hooks/useRestaurantStatus';
import { ChevronLeft, ChevronRight, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Menu = () => {
  const { addToCart } = useCart();
  const { loaded, isOpen, statusMessage } = useRestaurantStatus();
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [menuCategories, setMenuCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState(null);
  const [isCartDrawerOpen, setIsCartDrawerOpen] = useState(false);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);
  
  const categoryNavRef = useRef(null);

  // Fetch products from backend
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/products`);
        if (response.ok) {
          const data = await response.json();
          setMenuCategories(data);
          if (data.length > 0) {
            setActiveCategory(data[0].id);
          }
        }
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  // Check scroll arrows visibility for horizontal category nav
  const checkScrollArrows = () => {
    if (categoryNavRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = categoryNavRef.current;
      setShowLeftArrow(scrollLeft > 10);
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  useEffect(() => {
    const nav = categoryNavRef.current;
    if (nav) {
      nav.addEventListener('scroll', checkScrollArrows);
      checkScrollArrows();
      return () => nav.removeEventListener('scroll', checkScrollArrows);
    }
  }, []);

  // Switch to a category (tab-based)
  const switchToCategory = (categoryId) => {
    setActiveCategory(categoryId);
  };

  const scrollCategoryNav = (direction) => {
    if (categoryNavRef.current) {
      const scrollAmount = 200;
      categoryNavRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  const handleProductClick = (item, categoryName) => {
    setSelectedProduct(item);
    setSelectedCategory(categoryName);
  };

  const handleAddToCart = (product) => {
    addToCart(product);
    setIsCartDrawerOpen(true);
  };

  return (
    <div className="min-h-screen bg-white pb-32">
      {/* Temporary Notice */}
      <TemporaryNotice />
      
      {/* Closed Message — only after first fetch resolves, to avoid a
          false flash of "ZARVA" during the initial backend cold start */}
      {loaded && !isOpen && (
        <div className="bg-gradient-to-r from-red-50 to-rose-50 border-b-4 border-red-500 py-4">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center space-x-3">
              <AlertCircle className="w-8 h-8 text-red-600 flex-shrink-0" />
              <p className="text-lg font-bold text-red-900">{statusMessage}</p>
            </div>
          </div>
        </div>
      )}
      
      {/* Hero Section - Türkiz háttér */}
      <div className="bg-gradient-to-r from-[#00BCD4] to-[#00ACC1] py-16 text-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl sm:text-5xl font-black mb-3 text-white">Étlapunk</h1>
          <p className="text-lg text-white/90">Válaszd ki kedvenc ételeidet!</p>
        </div>
      </div>

      {/* Sticky Category Navigation Bar */}
      <div className="sticky top-20 z-30 bg-white shadow-md border-b border-gray-200">
        <div className="max-w-7xl mx-auto relative">
          {/* Left Scroll Arrow */}
          {showLeftArrow && (
            <button
              onClick={() => scrollCategoryNav('left')}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white shadow-lg rounded-full p-2 hover:bg-gray-50 transition-colors"
            >
              <ChevronLeft className="w-5 h-5 text-gray-700" />
            </button>
          )}

          {/* Category Pills */}
          <div
            ref={categoryNavRef}
            className="flex items-center gap-2 overflow-x-auto scrollbar-hide px-4 sm:px-6 lg:px-8 py-3 scroll-smooth"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {menuCategories.map((category) => (
              <button
                key={category.id}
                onClick={() => switchToCategory(category.id)}
                className={`flex-shrink-0 px-5 py-2.5 rounded-full font-bold text-sm transition-all duration-300 whitespace-nowrap ${
                  activeCategory === category.id
                    ? 'bg-[#00BCD4] text-white shadow-md'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {category.name}
              </button>
            ))}
          </div>

          {/* Right Scroll Arrow */}
          {showRightArrow && (
            <button
              onClick={() => scrollCategoryNav('right')}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white shadow-lg rounded-full p-2 hover:bg-gray-50 transition-colors"
            >
              <ChevronRight className="w-5 h-5 text-gray-700" />
            </button>
          )}
        </div>
      </div>

      {/* Menu Categories & Items - TAB BASED (Only show active category) */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {menuCategories
          .filter(category => category.id === activeCategory)
          .map((category) => (
          <div
            key={category.id}
            className="mb-16"
          >
            {/* Category Header */}
            <div className="mb-4">
              <h2 className="text-3xl font-black text-gray-900 mb-2">{category.name}</h2>
              {category.description && (
                <p className="text-base text-gray-600">{category.description}</p>
              )}
            </div>

            {/* Category Image */}
            {category.categoryImage && (
              <div className="mb-6 rounded-2xl overflow-hidden shadow-lg">
                <img
                  src={category.categoryImage}
                  alt={category.name}
                  className="w-full h-64 object-cover"
                />
              </div>
            )}

            {/* Product List */}
            <div className="bg-white rounded-2xl shadow-sm border-2 border-gray-100 overflow-hidden">
              {category.items.map((item, index) => {
                // Calculate display price
                let displayPrice = item.price || item.basePrice || 0;
                let priceLabel = `${displayPrice} Ft`;
                
                if (item.sizeOptions && item.sizeOptions.length > 0) {
                  const minPrice = Math.min(...item.sizeOptions.map(s => s.price));
                  priceLabel = `${minPrice} Ft-tól`;
                }

                const hasOptions = item.sizeOptions || item.toppingOptions || (item.extras && item.extras.length > 0) || item.hasNoteField || item.sideOptions;
                const isAvailable = item.isAvailable !== false; // Default to true if not specified

                return (
                  <div
                    key={item.id}
                    className={`${
                      index !== category.items.length - 1 ? 'border-b border-gray-100' : ''
                    }`}
                  >
                    <div
                      className={`relative p-4 transition-colors ${
                        isAvailable
                          ? 'hover:bg-gray-50 cursor-pointer'
                          : 'bg-red-50 border-l-4 border-red-500 grayscale opacity-70 cursor-not-allowed'
                      }`}
                      onClick={() => {
                        if (!isAvailable) return; // Block clicks on unavailable items
                        if (hasOptions) {
                          handleProductClick(item, category.name);
                        } else {
                          handleAddToCart({
                            ...item,
                            selectedOptions: [],
                            selectedExtras: [],
                            quantity: 1
                          });
                        }
                      }}
                    >
                      {!isAvailable && (
                        <div className="mb-3 inline-flex items-center px-3 py-1 bg-red-600 text-white text-xs font-black tracking-wide rounded-full shadow uppercase">
                          ⛔ Jelenleg nem elérhető
                        </div>
                      )}
                      <div className="flex justify-between items-start">
                        {/* Product Info */}
                        <div className="flex-1">
                          <h3 className={`text-lg font-bold mb-1 ${
                            isAvailable ? 'text-gray-900' : 'text-gray-500 line-through decoration-red-500 decoration-2'
                          }`}>
                            {item.name}
                            {item.unit && (
                              <span className="text-sm font-normal text-gray-500 ml-2">
                                {item.unit}
                              </span>
                            )}
                          </h3>
                          {item.description && (
                            <p className={`text-sm leading-relaxed ${
                              isAvailable ? 'text-gray-600' : 'text-gray-400'
                            }`}>
                              {item.description}
                            </p>
                          )}
                        </div>

                        {/* Price */}
                        <div className="ml-4 flex-shrink-0 text-right">
                          <p className={`text-xl font-black ${
                            isAvailable ? 'text-[#FFA726]' : 'text-gray-400 line-through decoration-red-500 decoration-2'
                          }`}>{priceLabel}</p>
                          {hasOptions && isAvailable && (
                            <button
                              className="mt-2 px-4 py-1.5 bg-[#00BCD4] text-white rounded-lg font-semibold text-xs hover:bg-[#00ACC1] transition-colors"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleProductClick(item, category.name);
                              }}
                            >
                              Választás
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Product Modal */}
      {selectedProduct && (
        <ProductModal
          product={selectedProduct}
          category={selectedCategory}
          onClose={() => setSelectedProduct(null)}
        />
      )}

      {/* Floating Cart Bar */}
      <FloatingCartBar onClick={() => setIsCartDrawerOpen(true)} />

      {/* Cart Drawer */}
      <CartDrawer
        isOpen={isCartDrawerOpen}
        onClose={() => setIsCartDrawerOpen(false)}
      />
    </div>
  );
};

export default Menu;
